<template>
  <div>
    <div
      class="freestyle-canvas-content container-fluid"
      :class="{
        'has-block-selecting': selectingBlocks && selectingBlocks.length > 0,
        'overflow-hidden': isPresentingOrBackstageOrShare || !selectingBlocks || selectingBlocks.length === 0
      }"
      v-if="slideActive.id && dataCanvasBlocks"
      @contextmenu.prevent="handleBlockRightClick($event)"
    >
      <MoveableBlockEditorV2
        v-for="(block, blockIndex) in blocksArray"
        @drag-start="onDragStart"
        @drag-end="onDragEnd"
        @selectBlock="onSelectBlock"
        @deselectBlock="onDeselectBlock"
        @duplicateBlock="onDuplicateBlocks"
        @focusEditor="onFocusEditor"
        @deleteBlock="onDeleteBlocks"
        @editImageBlock="onEditImageBlock"
        @openContextMenuBlock="onOpenContextMenuBlocks"
        :ref="`moveable-${block.id}`"
        :key="`${slideActive.id}-${block.id}`"
        :type="block.type"
        :block="{...block, index: blockIndex}"
        :block-id="block.id"
        :slide-id="slideActive.id"
        :elementGuidelines="elementGuidelines"
        :holdingShiftKey="holdingShiftKey"
        :holdingCtrlKey="holdingCtrlKey"
        :isPresentingOrBackstageOrShare="isPresentingOrBackstageOrShare"
        :draggingBlockId="draggingBlockId"
        :is-block-selecting="isBlockSelecting(block.id)"
        :isDraggingGroup="isDraggingGroup"
        :isMultipleBlocksSelecting="isMultipleBlocksSelecting"
        :selectingBlocks="selectingBlocks"
        @update-style="onUpdateStyleBlock"
        @update-content="onUpdateContentBlock"
      />

      <ContentSlideRightClickMenu
        :targetX="targetX"
        :targetY="targetY"
        :offsetX="offsetX"
        :offsetY="offsetY"
        :layerX="layerX"
        :layerY="layerY"
        :targetClick="targetClick"
        @copyBlock="handleCopyBlockContextMenu"
        @cutBlock="handleCutBlockContextMenu"
        @duplicateBlock="handleDuplicateBlockContextMenu"
        @changeBlockOrder="handleChangeBlockOrderContextMenu"
        @deleteBlock="handleDeleteBlockContextMenu"
      />
    </div>

    <!-- multiple blocks selection -->
    <Moveable
      v-if="groupMoveableVisible"
      :className="groupMoveableClasses"
      :target="selectingBlockElements"
      :key="key"
      :origin="false"
      :draggable="true"
      :edgeDraggable="true"
      :passDragArea="holdingCtrlKey || holdingShiftKey"
      :throttleDrag="2"
      :padding="{
        left: 5, top: 5, right: 5, bottom: 5,
      }"
      @dragGroup="onDragGroup"
      @dragGroupStart="onDragGroupStart"
      @dragGroupEnd="onDragGroupEnd"
    />
    <UploadImageV2
      :type="'contentSlideImage'"
      v-if="isUploadImageModalShowing"
      @hideModal="closeModalUploadImage"
      @callback="handleUploadFileFinished"
      :updateGifInBackground="handleUpdateGifInBackground"
      @selectExistImage="selectExistImage"
      :signedCFUrlExpiryDate="10*365"
    />

    <CropImageV2
      v-if="isCropImageModalShowing"
      :aspect-ratio="null"
      :cropper-data="cropperData"
      :image-url="imageUrl"
      :isChangingImage="isChangingImage"
      :initialAspectRatio="replaceImageRatio"
      @on-cancel="handleCancelCrop"
      @hideModal="isCropImageModalShowing = false"
      :updateInBackground="handleUpdateInBackground"
      :signedCFUrlExpiryDate="10*365"
    />
    <LinkModal />
  </div>
</template>

<script>
/* eslint-disable no-restricted-syntax */
import Vue from 'vue'

import { mapGetters, mapState } from 'vuex'
import Moveable from 'vue-moveable'
import _ from 'lodash'
import util from '@/util'
import transformUtils from '@/utils/transform.utils'
import MoveableBlockEditorV2 from '@/components-v2/presentation-editor/freestyle/MoveableBlockEditor.v3.vue'

import {
  DefaultCanvasBlocks,
  createNewTextBlock,
  createNewImageBlock,
  calculateImageDimension,
  calculateDefaultImageDimension,
  generateBlockId,
  createEmojiBlock,
  generateBlockHash,
  createShapeBlock,
  createLineBlock,
} from '@/utils/CanvasBlocksLayout'
import { centerImageTransform } from '@/utils/canvas-block-layouts/ImageLayout'
import EventBus from '@/eventBus'
import eventCategories from '@/constant/eventCategories'
import UndoRedoMixin from '@/mixins/undo-redo/UndoRedoMixin'
import LineEditorMixin from '@/mixins/freestyle/LineEditorMixin'
import ShapeEditorMixin from '@/mixins/freestyle/ShapeEditorMixin'
import ContentSlideShortcutKeysMixin from '@/mixins/freestyle/ContentSlideShortcutKeysMixin'
import ContentSlideRightClickMenu from '@/components-v2/presentation-editor/middle/right-click-menu/ContentSlideRightClickMenu.vue'
import ContentSlideImageUploadMixin from '@/mixins/ContentSlideImageUploadMixin'
import CanvasBlocksUpdateMixin from '@/mixins/presentation/CanvasBlocksUpdateMixin'
import UploadImageV2 from '@/components/presentations/UploadImage.v2.vue'
import CropImageV2 from '@/components/presentations/CropImage.v2.vue'
import BlockActions from './BlockActions.vue'
import LinkModal from '@/components-v2/presentation-editor/freestyle/LinkModal.vue'

const BlockActionsConstructor = Vue.extend(BlockActions)

export default {
  components: {
    MoveableBlockEditorV2,
    UploadImageV2,
    CropImageV2,
    Moveable,
    ContentSlideRightClickMenu,
    LinkModal,
  },
  mixins: [
    UndoRedoMixin,
    ContentSlideImageUploadMixin,
    CanvasBlocksUpdateMixin,
    ContentSlideShortcutKeysMixin,
    LineEditorMixin,
    ShapeEditorMixin,
  ],
  data() {
    return {
      slideActiveId: null,
      canvasBlocks: null,
      draggingBlockId: null,
      clipboardBlockId: null,
      readyCount: 0,
      blockNewestId: null,
      key: null,
      holdingShiftKey: false,
      holdingCtrlKey: false,
      isDraggingGroup: false,
      selectingBlockElements: [],
      changing: false,
      replaceImageRatio: null,
      targetX: null,
      targetY: null,
      offsetX: null,
      offsetY: null,
      layerX: null,
      layerY: null,
      targetClick: null,
      elementGuidelines: null,
    }
  },
  computed: {
    ...mapGetters(['isPresenting', 'isBackstage']),
    ...mapState({
      slideActive: (state) => state.slideActive,
      presentation: (state) => state.presentation,
      sharePresentation: (state) => state.sharePresentation,
      isSyncing: (state) => state.isSyncing,
      selectingBlocks: (state) => state.selectingBlocks,

    }),
    idSlideActive() {
      return this.slideActive.id
    },
    isPresentingOrBackstageOrShare() {
      return this.sharePresentation || this.isPresenting || this.isBackstage
    },

    isUsingDefaultCanvasBlocks() {
      return !this.slideActive.id
    },

    isBlockSelecting() {
      return (blockId) => {
        return this.selectingBlocks && this.selectingBlocks.some((block) => block.id === blockId)
      }
    },

    selectingBlockIds() {
      if (!this.selectingBlocks || this.selectingBlocks.length === 0) return []
      return this.key && [...this.selectingBlocks.map((b) => b.id)]
    },

    isMultipleBlocksSelecting() {
      return this.selectingBlockIds && this.selectingBlockIds.length > 1
    },

    multipleBlocksVisible() {
      return this.selectingBlocks && this.selectingBlocks.length > 0 &&
        this.dataCanvasBlocks &&
        this.selectingBlocks.every((block) => {
          return this.dataCanvasBlocks[block.id] && !this.dataCanvasBlocks[block.id].hidden
        })
    },
    groupMoveableVisible() {
      return this.isMultipleBlocksSelecting &&
        this.multipleBlocksVisible
    },

    groupMoveableClasses() {
      return `moveable-group ${this.changing ? 'changing' : ''}`
    },

    blocksArray() {
      if (!this.slideActive || !this.slideActive.id) {
        return []
      }

      return util.convertBlocksMapToArray(this.dataCanvasBlocks, 'id')
    },

    dataCanvasBlocks() {
      const slideActive = this.getSlideActive()

      // Nếu có canvasBlock thì hiển thị
      // Nếu ko có canvasBlockUrl thì sẽ kiểm tra tới canvasBlock
      //  +Nếu canvasBlock cũng ko có thì sẽ kiểm tra tới titleHTML, bodyHTML của data slide content cũ
      if (!slideActive.canvasBlocksUrl) {
        const canvasBlocks = DefaultCanvasBlocks(slideActive.id)
        if (!slideActive.canvasBlocks) {
          Object.keys(canvasBlocks).forEach((blockId) => {
            canvasBlocks[blockId].id = blockId
            const block = canvasBlocks[blockId]
            if (block.tag === 'heading' && slideActive.titleHTML) {
              canvasBlocks[blockId].content.value = slideActive.titleHTML
            }
            if (block.tag === 'content' && (slideActive.bodyHTML || slideActive.subheading)) {
              canvasBlocks[blockId].content.value = slideActive.bodyHTML || slideActive.subheading
            }
          })
          return canvasBlocks
        }
      }
      return slideActive.canvasBlocks
    },
  },

  watch: {
    idSlideActive(value, oldValue) {
      if (value !== oldValue) {
        this.readyCount = 0
        this.draggingBlockId = null
        this.blockNewestId = null
        this.$store.state.selectingBlocks = []
        this.holdingCtrlKey = false
        this.holdingShiftKey = false
        this.checkElementGuidelines()
      }
    },
    selectingBlockIds(value) {
      if (value && value.length > 1) {
        this.$nextTick(() => {
          this.selectingBlockElements = value.map((i) => document.getElementById(i))
          this.mountGroupBlockActions()
        })
      }
    },
    multipleBlocksVisible(value) {
      if (value) {
        this.key = Date.now()
        this.$nextTick(() => {
          this.mountGroupBlockActions()
        })
      }
    },
    dataCanvasBlocks(val, oldVal) {
      if (JSON.stringify(val) !== JSON.stringify(oldVal)) {
        this.checkElementGuidelines()
      }
    },
  },
  mounted() {
    this.listenBlockShortcutKeys()
    this.registerHistory()
    EventBus.$on('create-block', this.onCreateBlock)
    EventBus.$on('open-image-upload-modal', this.onEditImageBlock)
    EventBus.$on('right-click-paste-block', this.handlePasteBlock)
    EventBus.$on('blur-editing-box', this.resetEditingBlockId)
    this.listenLineUpdate()
    this.listenShapeUpdate()
    this.checkElementGuidelines()
  },
  destroyed() {
    EventBus.$off('create-block')
    EventBus.$off('blur-editing-box')
    EventBus.$off('open-image-upload-modal')
    EventBus.$off('right-click-paste-block')
    this.$store.state.selectingBlocks = []
  },

  beforeDestroy() {
    this.unListenBlockShortcutKeys()
    this.unlistenLineUpdate()
    this.unListenShapeUpdate()
    this.holdingCtrlKey = false
    this.holdingShiftKey = false
  },

  methods: {
    ...mapGetters(['getSlideActive']),

    onDragGroup({
      targets, events,
    }) {
      this.changing = true

      targets.forEach((t, i) => {
        const scale = this.$store.state.slideContainerScale
        const [deltaX, deltaY] = events[i].dist
        const [x, y] = events[i].translate
        const currentTranslate = events[i].transform
          .matchAll(/translate\((?<x>[+-]?([0-9]*[.])?[0-9]+)px,\s?(?<y>[+-]?([0-9]*[.])?[0-9]+)px\)/g)
        const translateCoords = {}
        let translateStr = ''
        /**  */
        for (const match of currentTranslate) {
          translateStr = match[0]
          if (match.groups && match.groups.x && match.groups.y) {
            translateCoords.x = match.groups.x
            translateCoords.y = match.groups.y
          }
          break
        }
        const newTranslate = `translate(${(+translateCoords.x + deltaX / scale + x) / 2}px, ${(+translateCoords.y + deltaY / scale + y) / 2}px)`
        const newTransform = events[i].transform.replace(translateStr, newTranslate)
        t.style.transform = newTransform
        EventBus.$emit(`update-moveable-control-${t.id}`)
      })
    },
    onDragGroupStart(e) {
      this.startDragTransforms = e.targets.map((t) => {
        return {
          transform: t.style.transform,
        }
      })
      this.isDraggingGroup = true
    },
    onDragGroupEnd({
      isDrag, targets, events,
    }) {
      this.changing = false

      if (this.hasABlockNotInsideCanvas(events)) {
        targets.forEach((t, i) => {
          t.style.transform = this.startDragTransforms[i].transform
          EventBus.$emit(`update-moveable-control-${t.id}`)
        })
        return
      }
      if (isDrag) {
        const oldCanvasBlocks = JSON.parse(JSON.stringify(this.dataCanvasBlocks))
        const newCanvasBlocks = { ...oldCanvasBlocks }
        const currentSelectingBlocks = this.selectingBlocks
        targets.forEach((target) => {
          newCanvasBlocks[target.id] = {
            ...newCanvasBlocks[target.id],
            style: {
              ...newCanvasBlocks[target.id].style,
              transform: target.style.transform,
            },
            h: generateBlockHash(),
          }
        })
        this.updateCanvasBlocks(newCanvasBlocks, this.slideActive.id)
        this.backupBlocks(oldCanvasBlocks, newCanvasBlocks, currentSelectingBlocks)
        this.updateGroupMoveableControl()
        util.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Move canvas blocks', null, this.slideActive.id, {
          isMultipleBlocksSelecting: this.isMultipleBlocksSelecting,
        })

        const selectingBlockIds = targets.map((t) => t.id)

        this.$store.state.selectingBlocks = this.blocksArray
          .filter((block) => selectingBlockIds.includes(block.id))
        this.isDraggingGroup = false
      }
    },

    hasABlockNotInsideCanvas(events) {
      return events.some((event) => {
        return transformUtils.isBlockNotInCanvas(event.target.id)
      })
    },

    checkElementGuidelines() {
      this.$nextTick(() => {
        this.elementGuidelines = Array.from(document.querySelectorAll('.freestyle-canvas-content .element-editor'))
      })
    },

    onDragStart(block) {
      this.draggingBlockId = block.id
      // Dont check guidelines for the original position of dragging block
      this.elementGuidelines = this.elementGuidelines.filter((elem) => elem.id !== block.id)
    },
    onDragEnd() {
      this.draggingBlockId = null
      this.checkElementGuidelines()
    },

    updateDataBlockByKey(key, blockId, blockData, isBackup = true, type) {
      if (isBackup) {
        this.backupUpdateDataBlockByKey({
          key,
          blockId,
          blockData,
        }, type)
      }
      const updatedCanvasBlocks = { ...this.dataCanvasBlocks }
      updatedCanvasBlocks[blockId][key] = {
        ...updatedCanvasBlocks[blockId][key],
        ...blockData,
      }
      updatedCanvasBlocks[blockId].h = generateBlockHash()

      this.updateCanvasBlocks(updatedCanvasBlocks, this.slideActive.id)
    },
    backupUpdateDataBlockByKey(newDataRaw, type) {
      const { key, blockId } = newDataRaw
      const oldBlockData = this.dataCanvasBlocks[blockId][key]
      const oldData = {
        keyData: key,
        blockId,
        blockData: oldBlockData,
      }
      const newData = {
        ...newDataRaw,
        keyData: key,
      }
      this.addActionToHistory({
        type: `${type}Block`,
        data: oldData,
        newData,
        slideActiveId: this.slideActive.id,
      })
    },
    resetEditingBlockId() {
      this.editingBlockId = null
    },
    onUpdateContentBlock(blockId, blockData) {
      this.updateDataBlockByKey('content', blockId, blockData, true, 'Content')
    },
    onUpdateStyleBlock(blockId, blockData) {
      const updatedBlock = {
        id: blockId,
        ...this.dataCanvasBlocks[blockId],
        style: {
          ...this.dataCanvasBlocks[blockId].style,
          ...blockData,
        },
        h: generateBlockHash(),
      }
      const oldCanvasBlocks = JSON.parse(JSON.stringify(this.dataCanvasBlocks))
      const newCanvasBlocks = {
        ...this.dataCanvasBlocks,
        [blockId]: updatedBlock,
      }
      this.updateCanvasBlocks(newCanvasBlocks, this.slideActive.id)
      this.forceActiveBlocks([updatedBlock])
      this.backupBlocks(oldCanvasBlocks, newCanvasBlocks)
    },
    onCreateBlock(type, data) {
      if (type === 'RichText') {
        this.onCreateTextBlock()
      } else if (type === 'Image') {
        this.replaceImageRatio = null
        this.openModalUploadImage(false)
      } else if (type === 'Emoji') {
        this.onCreateEmojiBlock(data)
      } else if (type === 'Shape') {
        this.onCreateShapeBlock(data)
      } else if (type === 'Line') {
        this.onCreateLineBlock(data)
      }
    },
    onCreateShapeBlock(shapeData) {
      const newBlock = createShapeBlock(this.slideActive.id, shapeData)
      const oldCanvasBlocks = JSON.parse(JSON.stringify(this.dataCanvasBlocks))
      const newCanvasBlocks = {
        ...this.dataCanvasBlocks,
        [newBlock.id]: newBlock,
      }
      this.updateCanvasBlocks(newCanvasBlocks, this.slideActive.id)
      this.blockNewestId = newBlock.id
      this.forceActiveBlocks([newBlock])
      this.backupBlocks(oldCanvasBlocks, newCanvasBlocks)
      util.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Insert shape block', this.slideActive.id, null, {
        shapeType: shapeData,
      })
    },
    onCreateLineBlock(data) {
      const newBlock = createLineBlock(this.slideActive.id, data)
      const oldCanvasBlocks = JSON.parse(JSON.stringify(this.dataCanvasBlocks))
      const newCanvasBlocks = {
        ...this.dataCanvasBlocks,
        [newBlock.id]: newBlock,
      }
      this.updateCanvasBlocks(newCanvasBlocks, this.slideActive.id)
      this.blockNewestId = newBlock.id
      this.forceActiveBlocks([newBlock])
      this.backupBlocks(oldCanvasBlocks, newCanvasBlocks)
      util.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Insert line block', this.slideActive.id, null, {
        lineType: data,
      })
    },
    onCreateTextBlock() {
      const newBlock = createNewTextBlock(this.slideActive.id)
      const oldCanvasBlocks = JSON.parse(JSON.stringify(this.dataCanvasBlocks))
      const newCanvasBlocks = {
        ...this.dataCanvasBlocks,
        [newBlock.id]: newBlock,
      }

      this.updateCanvasBlocks(newCanvasBlocks, this.slideActive.id)

      this.blockNewestId = newBlock.id
      this.editingBlockId = newBlock.id
      this.forceActiveBlocks([newBlock])
      setTimeout(() => {
        EventBus.$emit(`focus-editor-${newBlock.id}`)
      }, 50)
      this.backupBlocks(oldCanvasBlocks, newCanvasBlocks)
      util.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Insert text block', this.slideActive.id, null, {
        slideId: this.slideActive.id,
        isImportedFromPPT: this.slideActive.metadata && this.slideActive.metadata.isImportedFromPPT,
      })
    },
    onCreateImageBlock(imageData) {
      const newBlock = createNewImageBlock(this.slideActive.id, imageData.crop, imageData.cropperData)
      const imagePlaceholderBlock = this.blocksArray.filter((item) => item.type === 'Image' && item.content && item.content.isPlaceholderImage)[0]
      const oldCanvasBlocks = JSON.parse(JSON.stringify(this.dataCanvasBlocks))
      const newCanvasBlocks = {
        ...this.dataCanvasBlocks,
        [newBlock.id]: newBlock,
      }
      if (imagePlaceholderBlock) {
        delete newCanvasBlocks[imagePlaceholderBlock.id]
      }

      this.updateCanvasBlocks(newCanvasBlocks, this.slideActive.id)

      this.blockNewestId = newBlock.id
      this.forceActiveBlocks([newBlock])
      this.backupBlocks(oldCanvasBlocks, newCanvasBlocks)
      util.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Insert image block', this.slideActive.id, null, {
        slideId: this.slideActive.id,
        isImportedFromPPT: this.slideActive.metadata && this.slideActive.metadata.isImportedFromPPT,
      })
    },
    onCreateEmojiBlock(emojiData) {
      const newBlock = createEmojiBlock(this.slideActive.id, emojiData)
      const oldCanvasBlocks = JSON.parse(JSON.stringify(this.dataCanvasBlocks))
      const newCanvasBlocks = {
        ...this.dataCanvasBlocks,
        [newBlock.id]: newBlock,
      }
      this.updateCanvasBlocks(newCanvasBlocks, this.slideActive.id)
      this.blockNewestId = newBlock.id
      this.forceActiveBlocks([newBlock])
      this.backupBlocks(oldCanvasBlocks, newCanvasBlocks)
      util.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Insert emoji block', this.slideActive.id, emojiData.name, {
        slideId: this.slideActive.id,
        isImportedFromPPT: this.slideActive.metadata && this.slideActive.metadata.isImportedFromPPT,
      })
    },
    onReplaceImageBlock(data, isPlaceholderImage) {
      let { width, height } = isPlaceholderImage ? calculateDefaultImageDimension(data.cropperData)
        : calculateImageDimension(data.cropperData)
      const currentBlock = this.dataCanvasBlocks[this.replacingImageBlockId]
      const oldWidth = currentBlock.style.width.replace('px', '')
      const oldHeight = currentBlock.style.height.replace('px', '')

      const oldAspectRatio = oldWidth / oldHeight
      const newAspectRatio = width / height

      // accept some error here
      const roundedOldAspectRatio = Math.round(oldAspectRatio * 100)
      const roundedNewAsectRatio = Math.round(newAspectRatio * 100)

      // If the replaced image has (mostly) the same aspect ratio as the original img,
      // then we should keep the dimension (width x height) as well
      if (roundedOldAspectRatio === roundedNewAsectRatio) {
        width = oldWidth
        height = oldHeight
      }

      const newBlock = {
        ...currentBlock,
        id: this.replacingImageBlockId,
        style: {
          ...currentBlock.style,
          width: `${width}px`,
          height: `${height}px`,
        },
        content: {
          src: data.crop,
        },
        h: generateBlockHash(),
      }
      const isCenterImage = currentBlock.content.isPlaceholderImage

      if (isCenterImage) {
        newBlock.style.transform = centerImageTransform(width, height)
      }
      const oldCanvasBlocks = JSON.parse(JSON.stringify(this.dataCanvasBlocks))
      const newCanvasBlocks = {
        ...this.dataCanvasBlocks,
        [this.replacingImageBlockId]: newBlock,
      }

      this.updateCanvasBlocks(newCanvasBlocks, this.slideActive.id)

      this.forceActiveBlocks([newBlock])
      this.backupBlocks(oldCanvasBlocks, newCanvasBlocks)
    },
    hasSameContent(blocks, selectingBlocks) {
      if (!blocks || blocks.length == 0 || !selectingBlocks || selectingBlocks.length == 0) {
        return false
      }
      return blocks.every((block, index) => {
        return JSON.stringify(block.content) === JSON.stringify(selectingBlocks[index].content)
      })
    },

    onDuplicateBlocks(blocks) {
      if (this.$store.state.blockContentCache) {
        Object
          .keys(this.$store.state.blockContentCache)
          .filter((cacheKey) => cacheKey.startsWith(`${this.slideActive.id}:`))
          .forEach((cacheKey) => {
            const blockId = cacheKey.split(':')[1]
            const index = this.selectingBlocks.findIndex((b) => b.id === blockId)
            this.selectingBlocks[index] = {
              ...this.selectingBlocks[index],
              content: {
                value: this.$store.state.blockContentCache[cacheKey],
              },
            }
          })

        this.$store.state.blockContentCache = null
        this.editingBlockId = null
      }
      const currentSelectingBlocks = this.selectingBlocks
      const originBlocks = this.selectingBlocks && this.selectingBlocks.length > 0 ? this.selectingBlocks : blocks
      const clonedBlocks = originBlocks.map((block) => {
        const newBlockId = generateBlockId(this.slideActive.id)
        const newTransform = this._setTransformForClonedBlock(_.get(block, 'style.transform'))
        return {
          id: newBlockId,
          type: block.type,
          content: block.content,
          style: {
            ...block.style,
            transform: newTransform,
          },
          h: generateBlockHash(),
        }
      })
      const clonedBlocksMap = _.keyBy(clonedBlocks, 'id')
      const oldCanvasBlocks = JSON.parse(JSON.stringify(this.dataCanvasBlocks))
      const newCanvasBlocks = {
        ...this.dataCanvasBlocks,
        ...clonedBlocksMap,
      }
      this.updateCanvasBlocks(newCanvasBlocks, this.slideActive.id)
      this.forceActiveBlocks(clonedBlocks)

      this.backupBlocks(oldCanvasBlocks, newCanvasBlocks, currentSelectingBlocks)
    },

    onPasteBlocks(blocks) {
      const currentSelectingBlocks = this.selectingBlocks
      const pasteTheSameBlocks = this.hasSameContent(blocks, this.selectingBlocks)
      const originBlocks = pasteTheSameBlocks ? this.selectingBlocks : blocks
      const notSelectingAnyBlocksOnSameSlide = this.slideActive.id === this.$store.state.clipboardSlideId
        && (!this.selectingBlocks || this.selectingBlocks.length === 0)
      const clonedBlocks = originBlocks.map((block) => {
        const newBlockId = generateBlockId(this.slideActive.id)
        const newTransform = (pasteTheSameBlocks || notSelectingAnyBlocksOnSameSlide) ?
          this._setTransformForClonedBlock(_.get(block, 'style.transform')) :
          block.style.transform
        return {
          id: newBlockId,
          type: block.type,
          content: block.content,
          style: {
            ...block.style,
            transform: newTransform,
          },
          h: generateBlockHash(),
        }
      })
      const clonedBlocksMap = _.keyBy(clonedBlocks, 'id')
      const oldCanvasBlocks = JSON.parse(JSON.stringify(this.dataCanvasBlocks))
      const newCanvasBlocks = {
        ...this.dataCanvasBlocks,
        ...clonedBlocksMap,
      }
      this.updateCanvasBlocks(newCanvasBlocks, this.slideActive.id)
      this.forceActiveBlocks(clonedBlocks)

      this.backupBlocks(oldCanvasBlocks, newCanvasBlocks, currentSelectingBlocks)
    },

    bringToFront(block, trigger) {
      const oldBlocksArray = this.blocksArray
      const oldBlockIndex = oldBlocksArray.findIndex((b) => b.id === block.id)
      const newBlocksArray = util.arrayMove(oldBlocksArray, oldBlockIndex, this.blocksArray.length - 1)
      const oldCanvasBlocks = JSON.parse(JSON.stringify(this.dataCanvasBlocks))
      const newCanvasBlocks = _.keyBy(newBlocksArray, 'id')
      this.updateCanvasBlocks(newCanvasBlocks, this.slideActive.id)
      this.backupBlocks(oldCanvasBlocks, newCanvasBlocks, [block])
      util.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Bring to front', trigger, null, {
        slideId: this.slideActive.id,
      })
    },

    sendToBack(block, trigger) {
      const oldBlocksArray = this.blocksArray
      const oldBlockIndex = oldBlocksArray.findIndex((b) => b.id === block.id)
      const newBlocksArray = util.arrayMove(oldBlocksArray, oldBlockIndex, 0)
      const oldCanvasBlocks = JSON.parse(JSON.stringify(this.dataCanvasBlocks))
      const newCanvasBlocks = _.keyBy(newBlocksArray, 'id')
      this.updateCanvasBlocks(newCanvasBlocks, this.slideActive.id)
      this.backupBlocks(oldCanvasBlocks, newCanvasBlocks, [block])
      util.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Send to back', trigger, null, {
        slideId: this.slideActive.id,
      })
    },

    bringForward(block, trigger) {
      const oldBlocksArray = this.blocksArray
      const oldBlockIndex = oldBlocksArray.findIndex((b) => b.id === block.id)
      const newBlockIndex = (oldBlockIndex + 1 > this.blocksArray.length - 1) ?
        this.blocksArray.length - 1 :
        oldBlockIndex + 1
      const newBlocksArray = util.arrayMove(oldBlocksArray, oldBlockIndex, newBlockIndex)
      const oldCanvasBlocks = JSON.parse(JSON.stringify(this.dataCanvasBlocks))
      const newCanvasBlocks = _.keyBy(newBlocksArray, 'id')
      this.updateCanvasBlocks(newCanvasBlocks, this.slideActive.id)
      this.backupBlocks(oldCanvasBlocks, newCanvasBlocks, [block])
      util.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Bring forward', trigger, null, {
        slideId: this.slideActive.id,
      })
    },

    sendBackward(block, trigger) {
      const oldBlocksArray = this.blocksArray
      const oldBlockIndex = oldBlocksArray.findIndex((b) => b.id === block.id)
      const newBlockIndex = (oldBlockIndex - 1 < 0) ? 0 : oldBlockIndex - 1
      const newBlocksArray = util.arrayMove(oldBlocksArray, oldBlockIndex, newBlockIndex)
      const newCanvasBlocks = _.keyBy(newBlocksArray, 'id')
      const oldCanvasBlocks = JSON.parse(JSON.stringify(this.dataCanvasBlocks))
      this.updateCanvasBlocks(newCanvasBlocks, this.slideActive.id)
      this.backupBlocks(oldCanvasBlocks, newCanvasBlocks, [block])
      util.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Send backward', trigger, null, {
        slideId: this.slideActive.id,
      })
    },

    backupBlocks(oldData, newData, previousSelectingBlocks) {
      this.addActionToHistory({
        type: 'UpdateBlocks',
        data: {
          canvasBlocks: oldData,
          selectingBlocks: previousSelectingBlocks,
          slideActiveId: this.slideActive.id,
        },
        newData: {
          canvasBlocks: newData,
          selectingBlocks: previousSelectingBlocks,
          slideActiveId: this.slideActive.id,
        },
        slideActiveId: this.slideActive.id,
      })
    },

    updateGroupMoveableControl() {
      this.key = Date.now()
      this.mountGroupBlockActions()
    },

    async restoreContentBlock(data) {
      await this.restoreUpdateDataBlockByKey(data)
    },
    async restoreUpdateBlocks(data) {
      this.updateCanvasBlocks(data.canvasBlocks, data.slideActiveId)

      this.$nextTick(() => {
        if (data.selectingBlocks) {
          this.$store.state.selectingBlocks = data.selectingBlocks
        }
        this.updateGroupMoveableControl()
      })
    },

    async restoreDragBlock(data) {
      await this.restoreUpdateDataBlockByKey(data)
    },
    async restoreRotateBlock(data) {
      await this.restoreUpdateDataBlockByKey(data)
    },
    async restoreResizeBlock(data) {
      await this.restoreUpdateDataBlockByKey(data)
    },

    async restoreUpdateDataBlockByKey(data) {
      const {
        keyData: key,
        blockId,
        blockData,
      } = data
      await this.updateDataBlockByKey(key, blockId, blockData, false)
    },

    forceActiveBlocks(blocks) {
      setTimeout(() => {
        this.$store.state.selectingBlocks = blocks
        this.key = Date.now()
        this.holdingCtrlKey = false
        this.holdingShiftKey = false
      }, 50)
    },

    onFocusEditor(blockId) {
      this.editingBlockId = blockId
    },
    onDeleteBlocks(blocks) {
      if (!blocks || blocks.length === 0) return
      const oldCanvasBlocks = JSON.parse(JSON.stringify(this.dataCanvasBlocks))
      const newCanvasBlocks = {
        ...this.dataCanvasBlocks,
      }
      blocks.forEach((block) => {
        if (newCanvasBlocks[block.id]) {
          delete newCanvasBlocks[block.id]
        }
      })
      this.updateCanvasBlocks(newCanvasBlocks, this.slideActive.id)
      this.backupBlocks(oldCanvasBlocks, newCanvasBlocks, blocks)
      this.$store.state.selectingBlocks = []
      this.key = Date.now()
    },
    onEditImageBlock(blockId, blockStyle) {
      this.openModalUploadImage(true)
      this.replacingImageBlockId = blockId
      this.isChangingPlaceholderImage = true
      if (blockStyle) {
        const blockWidth = blockStyle.width.replace('px', '')
        const blockHeight = blockStyle.height.replace('px', '')
        this.replaceImageRatio = blockWidth / blockHeight
      }
    },
    onOpenContextMenuBlocks(event, trigger) {
      if (!this.slideActive.id) return
      if (event) {
        this.targetX = event.x
        this.targetY = event.y
        this.offsetX = event.offsetX
        this.offsetY = event.offsetY
        this.layerX = event.layerX
        this.layerY = event.layerY
        this.targetClick = event.target
      }
      EventBus.$emit('canvas-right-click-menu: hide')
      EventBus.$emit('content-slide-right-click-menu: show')
      util.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Open block context menu',
        trigger,
      )
    },

    handleBlockRightClick(event) {
      if (this.isPresentingOrBackstageOrShare) return
      if (this.editingBlockId) return
      const selectingBlock = event.target.closest('.element-editor')
      if (!selectingBlock) return
      const blockId = selectingBlock.id
      if (!blockId) return
      const block = this.dataCanvasBlocks[blockId]
      if (block.content && block.content.isPlaceholderImage) return
      this.onSelectBlock(block)
      this.onOpenContextMenuBlocks(event, 'right-click')
    },

    onSelectBlock(block) {
      if (!this.holdingCtrlKey && !this.holdingShiftKey) {
        this.$store.state.selectingBlocks = [block]
      } else if (this.selectingBlockIds.includes(block.id)) {
        if (this.editingBlockId) {
          this.editingBlockId = null
          EventBus.$emit('show-freestyle-editor-toolbar', false)
        }
        const index = this.selectingBlockIds.indexOf(block.id)
        this.$store.state.selectingBlocks = [
          ...this.selectingBlocks.slice(0, index),
          ...this.selectingBlocks.slice(index + 1),
        ]
      } else {
        this.$store.state.selectingBlocks = [
          ...this.selectingBlocks,
          block,
        ]
      }
    },
    onDeselectBlock() {
      if (this.holdingCtrlKey || this.holdingShiftKey) {
        return
      }
      this.$store.state.selectingBlocks = []
      this.editingBlockId = null
    },

    mountGroupBlockActions() {
      const self = this
      const groupTopEdge = document.querySelector('.moveable-group > .moveable-line')
      if (groupTopEdge) {
        const blockActionWrapper = document.createElement('div')
        const child = groupTopEdge.children
        if (child && child[0]) {
          groupTopEdge.replaceChild(blockActionWrapper, child[0])
        } else {
          groupTopEdge.appendChild(blockActionWrapper)
        }
        const BlockActionsInstance = new BlockActionsConstructor({
          propsData: {
            onClickDuplicateBlock: self.onClickDuplicateGroup,
            onClickDeleteBlock: self.onClickDeleteGroup,
            blockData: self.selectingBlocks,
          },
        })
        BlockActionsInstance.$mount(blockActionWrapper)
      }
    },

    onClickDuplicateGroup() {
      this.onDuplicateBlocks(this.selectingBlocks)
      util.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Duplicate canvas blocks', 'Duplicate icon', this.slideActive.id, {
        isMultipleBlocksSelecting: this.isMultipleBlocksSelecting,
      })
    },
    onClickDeleteGroup() {
      this.onDeleteBlocks(this.selectingBlocks)
      util.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Delete canvas blocks', 'Delete icon', this.slideActive.id, {
        isMultipleBlocksSelecting: this.isMultipleBlocksSelecting,
        slideId: this.slideActive.id,
      })
    },

    _setTransformForClonedBlock(transform) {
      if (!transform) return ''
      return transform.replace(/([+-]?([0-9]*[.])?[0-9]+)px+/g, (number) => {
        return `${parseInt(number, 10) + 30}px`
      })
    },

    handleCropImageFinished(data) {
      if (!data.isChangingImage) {
        this.onCreateImageBlock(data)
      } else {
        this.onReplaceImageBlock(data, this.isChangingPlaceholderImage)
      }
      this.isCropImageModalShowing = false
      this.isChangingPlaceholderImage = false
    },

    handleCopyBlockContextMenu() {
      this.copyBlocks('context-menu')
    },

    handleCutBlockContextMenu() {
      this.cutBlocks('context-menu')
    },

    handleDuplicateBlockContextMenu() {
      this.duplicateBlocks('context-menu')
    },

    handleChangeBlockOrderContextMenu(command) {
      if (this.isChangeBlockOrderDisabled) return
      this[command](this.selectingBlocks[0], 'context-menu')
    },

    handleDeleteBlockContextMenu() {
      this.deleteBlocks('context-menu')
    },

    handlePasteBlock() {
      this.pasteBlock('context-menu')
    },
  },
}
</script>

<style lang="scss" scoped>
.freestyle-canvas-content {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  flex: 1;
  z-index: 119;
  padding-top: 60px;
  &.has-block-selecting {
    z-index: 130; // When selecting a block, make it on the top, so that we can click Duplicate/Delete blocks
  }
  .remove-block--disabled::v-deep {
    .block-action-icon.__delete {
      opacity: 0.4;
      &:hover {
        cursor: not-allowed;
      }
    }
  }
}
.moveable-group-container {
  z-index: 131;
}

.moveable-group {
  &::v-deep .moveable-control-box {
    .moveable-line {
      display: none;
    }
  }
  &.changing ::v-deep .moveable-area:hover {
    cursor: move;
  }
  &.changing ::v-deep .moveable-line:hover {
    cursor: move;
  }
  &::v-deep .moveable-line {
    background: #088efc !important;
    height: 3px !important;
    &:hover {
      cursor: move;
    }
  }
}

</style>
